import { Box, ButtonBase, Typography } from "@mui/material";
import { PlusCircleIcon, QrCodeIcon, WithdrawIcon } from "../../../icons";
import { ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { Skeleton } from "../../shared";
import { NetworkPickerDrawer } from "./NetworkPickerDrawer";
import { MethodDrawer } from "./DepositMethodDrawer";
import { AssetsSearchDrawer } from "./WalletAssetsList";
import { cryptoNetworkToDepositNetwork, DepositNetwork } from "./networks";
import {
  getCryptoOverview,
  type CryptoOverviewItem,
} from "../../../services/crypto";
import { fetchExchangeRate } from "../../../services/exchange-rate";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";
import { P2pTransferDrawer } from "../p2p/P2pTransferDrawer";

interface ActionButtonProps {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}

function ActionButton({ icon, label, onClick, disabled }: ActionButtonProps) {
  return (
    <ButtonBase
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      sx={(theme) => ({
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        padding: "18px 8px",
        borderRadius: "20px",
        backgroundColor: "secondary.main",
        color: "text.primary",
        border:
          theme.palette.mode === "light"
            ? "1px solid rgba(0, 0, 0, 0.06)"
            : "none",
        transition: "opacity 0.2s ease-in-out",
        opacity: disabled ? 0.4 : 1,
        "&:active": {
          transform: disabled ? "none" : "scale(0.97)",
        },
      })}
    >
      <Box
        sx={{
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{label}</Typography>
    </ButtonBase>
  );
}

interface WalletActionButtonsProps {
  isLoading?: boolean;
}

/**
 * Верхний ряд кнопок на главной: QR-код · Пополнить · Вывести.
 *
 * И «Пополнить», и «Вывести» реализованы через единый 3-шаговый flow:
 *   1) Способ (Криптовалюта / За фиат — «скоро»)
 *   2) Выбор актива (USDT / BTC / …)
 *   3) Если у актива >1 сети — выбор сети; иначе сразу /deposit или /withdraw.
 */
export function WalletActionButtons({ isLoading }: WalletActionButtonsProps) {
  const navigate = useNavigate();
  const { isFeatureEnabled, guardFeature } = useFeatureFlags();
  const { t } = useTranslation("home");

  // Единый state-machine на оба флоу — только цель разная.
  type FlowStep = null | "method" | "asset" | "network";
  type FlowKind = "deposit" | "withdraw";
  const [flow, setFlow] = useState<FlowKind>("deposit");
  const [step, setStep] = useState<FlowStep>(null);
  const [selectedAsset, setSelectedAsset] = useState<CryptoOverviewItem | null>(
    null,
  );
  const [p2pOpen, setP2pOpen] = useState(false);

  const { data: overviewData } = useQuery({
    queryFn: getCryptoOverview,
    queryKey: ["crypto-overview"],
    staleTime: 30_000,
  });
  const { data: usdtRate } = useQuery({
    queryFn: () =>
      fetchExchangeRate({ fromCurrency: "USDT", toCurrency: "RUB", sellAmount: 1 }),
    queryKey: ["rate", "USDT", "RUB"],
    staleTime: 60_000,
  });
  const usdtRub = Number(usdtRate?.show_rate ?? usdtRate?.rate ?? 0) || 0;
  const rateToRub: Record<string, number> = {
    USDT: usdtRub,
    BTC: usdtRub * 100_000,
    ETH: usdtRub * 3_500,
    SOL: usdtRub * 200,
  };

  const selectedAssetNetworks: DepositNetwork[] = useMemo(() => {
    if (!selectedAsset) return [];
    return selectedAsset.networks.map((n) =>
      cryptoNetworkToDepositNetwork(selectedAsset, n),
    );
  }, [selectedAsset]);

  const closeFlow = () => {
    setStep(null);
    setSelectedAsset(null);
  };

  const startFlow = (kind: FlowKind) => {
    setFlow(kind);
    setSelectedAsset(null);
    setStep("method");
  };

  const handleAssetSelect = (item: CryptoOverviewItem) => {
    if (item.networks.length === 0) return;

    // Fast-path: если у актива одна сеть — открываем страницу депозита/вывода
    // без промежуточного шага (как в Bybit/OKX/Trust).
    if (item.networks.length === 1) {
      const n = item.networks[0];
      closeFlow();
      const path = flow === "withdraw" ? "/withdraw" : "/deposit";
      navigate(
        `${path}?network=${encodeURIComponent(n.code)}&currency=${encodeURIComponent(item.code)}`,
      );
      return;
    }
    setSelectedAsset(item);
    setStep("network");
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "10px",
        }}
      >
        {[0, 1, 2].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={92}
            sx={{ borderRadius: "20px" }}
          />
        ))}
      </Box>
    );
  }

  const overviewItems = overviewData ?? [];

  // Тексты на шагах — разные для пополнения и вывода.
  const methodProps =
    flow === "withdraw"
      ? {
          title: t("flows.withdraw_method_title"),
          subtitle: t("flows.withdraw_method_subtitle"),
          cryptoSubtitle: t("flows.crypto_subtitle"),
          fiatSubtitle: t("flows.fiat_subtitle_withdraw"),
        }
      : {
          title: t("flows.deposit_method_title"),
          subtitle: t("flows.deposit_method_subtitle"),
          cryptoSubtitle: t("flows.crypto_subtitle"),
          fiatSubtitle: t("flows.fiat_subtitle_deposit"),
        };

  const assetProps =
    flow === "withdraw"
      ? {
          title: t("flows.select_currency"),
          subtitle: t("flows.withdraw_label"),
        }
      : {
          title: t("flows.select_currency"),
          subtitle: t("flows.deposit_label"),
        };

  const networkTitle = selectedAsset
    ? t("flows.networks_of", { code: selectedAsset.code })
    : undefined;
  const networkSubtitle =
    flow === "withdraw"
      ? t("flows.network_hint_withdraw")
      : t("flows.network_hint_deposit");

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "10px",
        }}
      >
        <ActionButton
          icon={<QrCodeIcon />}
          label={t("actions.qr")}
          disabled={!isFeatureEnabled("sbp_qr")}
          onClick={() => {
            if (!guardFeature("sbp_qr")) return;
            navigate("/pay-qr");
          }}
        />
        <ActionButton
          icon={<PlusCircleIcon />}
          label={t("actions.receive")}
          disabled={!isFeatureEnabled("deposit")}
          onClick={() => {
            if (!guardFeature("deposit")) return;
            startFlow("deposit");
          }}
        />
        <ActionButton
          icon={<WithdrawIcon />}
          label={t("actions.withdraw")}
          disabled={!isFeatureEnabled("withdraw")}
          onClick={() => {
            if (!guardFeature("withdraw")) return;
            startFlow("withdraw");
          }}
        />
      </Box>

      {/* Шаг 1. Способ */}
      <MethodDrawer
        open={step === "method"}
        onClose={closeFlow}
        onSelectCrypto={() => setStep("asset")}
        onSelectP2p={
          flow === "withdraw"
            ? () => {
                closeFlow();
                setP2pOpen(true);
              }
            : undefined
        }
        isWithdraw={flow === "withdraw"}
        {...methodProps}
      />

      {/* P2P-перевод — отдельный drawer поверх */}
      <P2pTransferDrawer
        open={p2pOpen}
        onClose={() => setP2pOpen(false)}
      />

      {/* Шаг 2. Выбор валюты */}
      <AssetsSearchDrawer
        open={step === "asset"}
        onClose={closeFlow}
        items={overviewItems}
        rateToRub={rateToRub}
        title={assetProps.title}
        subtitle={assetProps.subtitle}
        onSelectAsset={handleAssetSelect}
      />

      {/* Шаг 3. Выбор сети */}
      <NetworkPickerDrawer
        open={step === "network"}
        onClose={closeFlow}
        mode={flow}
        networksOverride={selectedAssetNetworks}
        title={networkTitle}
        subtitle={networkSubtitle}
      />
    </>
  );
}
